import type { FC } from 'react';
import React, { memo, useState, useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import ModalDialog, {
	ModalBody,
	ModalTitle,
	ModalHeader,
	ModalFooter,
} from '@atlaskit/modal-dialog';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import { Checkbox } from '@atlaskit/checkbox';
import { Flex } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import type { Template } from '@confluence/template-list/entry-points/TemplateListQuery.ts';
import { getTemplateIdAttributesForAnalytics } from '@confluence/template-utils/entry-points/templateUtils.ts';
import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n.ts';

// for analytics
const SCREEN_NAME = 'overwriteWarningModal';

const i18nBase = defineMessages({
	overwriteWarningModalDialogTitle: {
		id: 'template-panel.overwrite-warning-modal.title',
		defaultMessage: 'Select this template?',
		description:
			'The title of the modal dialog that opens when a user selects another template to use in the editor, but already has user content',
	},
	overwriteWarningModalDialogBodyText: {
		id: 'template-panel.overwrite-warning-modal.body',
		defaultMessage: 'Existing content will be removed and replaced with the selected template.',
		description:
			'The text inside the body of the modal dialog that opens when a user selects a template to use and already has content in the editor',
	},
	overwriteWarningCheckBoxText: {
		id: 'template-panel.overwrite-warning-modal.checkbox.description',
		defaultMessage: 'Or, keep this page and start new page',
		description:
			'The text to the right of a checkbox which allows a user to keep their content and create a new page with the template if checked. Else if not checked, it will replace the current page with the template content',
	},
	overwriteWarningCancelButtonText: {
		id: 'template-panel.overwrite-warning-modal.button.cancel',
		defaultMessage: 'Cancel',
		description:
			'Button text to close the modal dialog that opens when a user selects a template to use and already has content in the editor',
	},
	overwriteWarningConfirmButtonText: {
		id: 'template-panel.overwrite-warning-modal.button.confirm',
		defaultMessage: 'Confirm',
		description:
			'Button text to move to the next step of using a template in the modal dialog that opens when a user selects a template to use and already has content in the editor',
	},
});

const i18nLiveDocs = defineMessages({
	overwriteWarningCheckBoxText: {
		id: 'template-panel.overwrite-warning-modal.checkbox.description.livedocs',
		defaultMessage: 'Or, keep this content and start a new page or doc',
		description: 'Updated version of template-panel.overwrite-warning-modal.checkbox.description',
	},
});

const i18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);

export type OverwriteWarningModalProps = {
	onCreate: (template: Template, replaceContent?: boolean) => void;
	onClose: () => void;
	template: Template;
};

export const OverwriteWarningModal: FC<OverwriteWarningModalProps> = memo(
	({ onClose, onCreate, template }) => {
		const [createNewPage, setCreateNewPage] = useState(false);
		const isLivePage = useIsCurrentPageLive();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		useEffect(() => {
			// screen event should be triggered once
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: SCREEN_NAME,
					attributes: {
						...getTemplateIdAttributesForAnalytics(template),
						isLivePage,
					},
				},
			}).fire();
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		const handleReplaceCurrentPageToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'toggled',
					actionSubject: 'option',
					actionSubjectId: 'createNewPageOption',
					source: SCREEN_NAME,
					attributes: {
						createNewPage,
						isLivePage,
					},
				},
			}).fire();

			setCreateNewPage(event.target.checked);
		};

		const handleOnClickCancel = () => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'overwriteWarningModalCancel',
					source: SCREEN_NAME,
					attributes: {
						isLivePage,
					},
				},
			}).fire();

			onClose();
		};

		const handleOnClickConfirm = () => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'overwriteWarningModalConfirm',
					source: SCREEN_NAME,
					attributes: {
						createNewPage,
						...getTemplateIdAttributesForAnalytics(template),
						isLivePage,
					},
				},
			}).fire();

			onCreate(template, !createNewPage);
			// need to close the dialog because it overrides the blueprint wizard's input focus
			onClose();
		};

		return (
			<ModalDialog onClose={handleOnClickCancel} width={400} testId="overwrite-warning-modal">
				<ModalHeader hasCloseButton>
					<ModalTitle>
						<FormattedMessage {...i18n.overwriteWarningModalDialogTitle} />
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<Flex direction="column" gap="space.200">
						<FormattedMessage {...i18n.overwriteWarningModalDialogBodyText} />
						<Checkbox
							testId="overwrite-warning-modal-create-new-page-checkbox"
							label={<FormattedMessage {...i18n.overwriteWarningCheckBoxText} />}
							onChange={handleReplaceCurrentPageToggle}
							name="overwrite-warning-modal-create-new-page-checkbox"
						/>
					</Flex>
				</ModalBody>
				<ModalFooter>
					<ButtonGroup>
						<Button
							testId="overwrite-warning-modal-button-cancel"
							onClick={handleOnClickCancel}
							appearance="subtle"
						>
							<FormattedMessage {...i18n.overwriteWarningCancelButtonText} />
						</Button>
						<Button
							testId="overwrite-warning-modal-button-confirm"
							onClick={handleOnClickConfirm}
							appearance="primary"
						>
							<FormattedMessage {...i18n.overwriteWarningConfirmButtonText} />
						</Button>
					</ButtonGroup>
				</ModalFooter>
			</ModalDialog>
		);
	},
);
